import { SELECT_PIPELINE, SET_DEAL_STAGE_MAPPINGS, SET_FORECAST_CATEGORIES, SET_IS_CREATE_FORECAST_TYPE_WIZARD_OPEN } from './ActionTypes';
import { updatePipelineMapping } from '../api/forecastApi';
import { updateForecastCategories } from '../api/propertiesApi';
import { getDealStageMappings, getExistingForecastCategories, getForecastCategories } from '../selectors/wizardSelectors';
import { buildMapping, convertForecastCategoryValues, createForecastCategoryPayload, sortDealStageIds } from '../utils/wizardUtils';
import { HAS_SETUP_FORECAST_CATEGORIES } from '../constants/SettingsTypes';
import { setPortalSetting } from '../api/portalSettingsApi';
import { OMIT } from '../constants/ForecastCategories';
export function selectPipeline(pipeline) {
  return dispatch => dispatch({
    type: SELECT_PIPELINE,
    payload: pipeline
  });
}
export function setForecastCategories(forecastCategories) {
  return dispatch => {
    dispatch({
      type: SET_FORECAST_CATEGORIES,
      payload: {
        existing: forecastCategories,
        updated: forecastCategories
      }
    });
  };
}
export function setForecastCategory(existingValue, label, description, newValue) {
  return (dispatch, getState) => {
    const state = getState();
    const forecastCategories = getForecastCategories(state);
    const existingForecastCategories = getExistingForecastCategories(state);
    const index = forecastCategories.findIndex(forecastCategory => forecastCategory ? forecastCategory.value === existingValue : false);
    const updatedForecastCategories = [...forecastCategories];
    if (index !== -1) {
      let overrideNewValue = newValue;
      let overrideDescription = description;
      if (overrideNewValue) {
        const existingForecastCategory = existingForecastCategories.find(forecastCategory => forecastCategory.value === existingValue);
        if (existingForecastCategory && existingForecastCategory.label.toLowerCase() === label.toLowerCase()) {
          overrideNewValue = false;
          overrideDescription = existingForecastCategory.description;
        }
      }
      updatedForecastCategories[index] = Object.assign({}, updatedForecastCategories[index], {
        label,
        description: overrideDescription,
        useNewValue: overrideNewValue
      });
    } else {
      const closedWon = updatedForecastCategories.pop();
      updatedForecastCategories.push({
        description,
        displayOrder: updatedForecastCategories.length + 1,
        doubleData: 0,
        hidden: false,
        label,
        readOnly: false,
        value: existingValue
      });
      if (closedWon) {
        closedWon.displayOrder = updatedForecastCategories.length + 1;
        updatedForecastCategories.push(closedWon);
      }
    }
    dispatch({
      type: SET_FORECAST_CATEGORIES,
      payload: {
        updated: updatedForecastCategories
      }
    });
  };
}
export function removeForecastCategories(categoriesToRemove) {
  return (dispatch, getState) => {
    const forecastCategories = getForecastCategories(getState());
    const updatedForecastCategories = forecastCategories.filter(category => !categoriesToRemove.includes(category.value)).map((category, index) => {
      return Object.assign({}, category, {
        displayOrder: index + 1
      });
    });
    dispatch({
      type: SET_FORECAST_CATEGORIES,
      payload: {
        updated: updatedForecastCategories
      }
    });
  };
}
export function setDealStageMappings(dealStageMappings) {
  return dispatch => {
    dispatch({
      type: SET_DEAL_STAGE_MAPPINGS,
      payload: dealStageMappings
    });
  };
}
export function setDealStageMapping(pipelineId, automate, forecastCategoryId, newMappings) {
  return (dispatch, getState) => {
    const dealStageMappings = getDealStageMappings(getState());
    const updatedDealStageMappings = Object.assign({}, dealStageMappings);
    const allDealStages = dealStageMappings[pipelineId].allDealStages;

    // Update automation status - only update mappings if set to automate
    updatedDealStageMappings[pipelineId].automate = automate;
    if (automate && forecastCategoryId && newMappings) {
      const mappedDealStages = [];
      // Set selected deal stage mappings for given pipeline and forecast category
      updatedDealStageMappings[pipelineId].dealStageByForecastCategory[forecastCategoryId] = sortDealStageIds(allDealStages, newMappings);
      // Iterate through all mappings for the changed pipeline to make sure
      // that a deal stage is only used for one forecast category
      for (const [key, value] of Object.entries(updatedDealStageMappings[pipelineId].dealStageByForecastCategory)) {
        const duplicateMappings = value.filter(val => newMappings.includes(val));
        // If the context forecast category is not the one we changed
        // and it contains any of the same deal stage mappings then
        // remove those deal stage mappings so only changed one has them
        if (key !== forecastCategoryId && duplicateMappings.length > 0) {
          const uniqueMappings = value.filter(val => !newMappings.includes(val));
          updatedDealStageMappings[pipelineId].dealStageByForecastCategory[key] = uniqueMappings;
        }
        mappedDealStages.push(...value);
      }
      // Put unused mappings into the OMIT bucket
      const unmappedDealStages = allDealStages.filter(({
        stageId
      }) => !mappedDealStages.includes(stageId)).map(({
        stageId
      }) => stageId);
      const omitDealStages = sortDealStageIds(allDealStages, [...updatedDealStageMappings[pipelineId].dealStageByForecastCategory[OMIT], ...unmappedDealStages]);
      updatedDealStageMappings[pipelineId].dealStageByForecastCategory[OMIT] = omitDealStages;
    }
    dispatch({
      type: SET_DEAL_STAGE_MAPPINGS,
      payload: updatedDealStageMappings
    });
  };
}
export function saveWizard(dealStageMappings) {
  return (dispatch, getState) => {
    const state = getState();
    const forecastCategories = convertForecastCategoryValues(getForecastCategories(state));
    const mappings = Object.keys(dealStageMappings).map(pipelineId => buildMapping(pipelineId, forecastCategories, dealStageMappings[pipelineId])).filter(mapping => mapping);
    const forecastCategoriesPayload = createForecastCategoryPayload(forecastCategories);
    return updateForecastCategories(forecastCategoriesPayload).then(() => {
      return Promise.all([...mappings.map(mapping => updatePipelineMapping(mapping)), setPortalSetting(HAS_SETUP_FORECAST_CATEGORIES, true)]);
    });
  };
}
export function setIsCreateForecastTypeWizardOpen(isOpen) {
  return dispatch => {
    dispatch({
      type: SET_IS_CREATE_FORECAST_TYPE_WIZARD_OPEN,
      payload: isOpen
    });
  };
}